/**
 * Module #36 — Reports hook. Wraps the IE report engine:
 *   GET  /v1/reports/datasets — UI-safe dataset + field metadata for the builder
 *   POST /v1/reports/run      — run a spec → columns + rows
 *   POST /v1/reports/export   — run a spec → base64 CSV (triggers a download)
 *
 * Pure data, no LLM. Tenant scope is forced server-side from the JWT.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from './useSession';
import { fetchWithAuth } from '../lib/accessToken';

export type Aggregation = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'count_distinct';
export type FilterOp =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'contains'
  | 'is_null'
  | 'is_not_null'
  | 'between';

export interface ReportFieldMeta {
  key: string;
  label: string;
  type: string;
  description?: string;
  filterable: boolean;
  sortable: boolean;
  groupable: boolean;
  measure: boolean;
  aggregations?: Aggregation[];
  enumValues?: string[];
}

export interface ReportDatasetMeta {
  key: string;
  label: string;
  description: string;
  fields: ReportFieldMeta[];
}

export interface SpecColumn {
  field: string;
  agg?: Aggregation;
}
export interface SpecFilter {
  field: string;
  op: FilterOp;
  value?: unknown;
}
export interface ReportSpec {
  dataset: string;
  columns: SpecColumn[];
  filters?: SpecFilter[];
  groupBy?: string[];
  sort?: { field: string; dir: 'asc' | 'desc' }[];
  windowDays?: number;
  limit?: number;
}

export interface ReportColumn {
  key: string;
  label: string;
  type: string;
}
export interface RunResult {
  dataset: string;
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  rowCount: number;
  truncated: boolean;
}

/** A named report (preset) — a ready-to-run spec over a dataset. */
export interface ReportPreset {
  key: string;
  label: string;
  dataset: string;
  description: string;
  /** Non-null when the data behind it needs setup first (tax profile, settlement run). */
  needs: string | null;
  spec: ReportSpec;
}

export type LoadStatus = { kind: 'idle' } | { kind: 'loading' } | { kind: 'error'; detail: string };

type RunOutcome = { ok: true; result: RunResult } | { ok: false; detail: string };

export interface UseReportsReturn {
  datasets: ReportDatasetMeta[];
  presets: ReportPreset[];
  status: LoadStatus;
  runReport: (spec: ReportSpec) => Promise<RunOutcome>;
  exportCsv: (spec: ReportSpec) => Promise<{ ok: boolean; detail?: string }>;
}

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

interface ApiError {
  error: string;
  detail: string;
}

function triggerCsvDownload(base64: string, filename: string): void {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

export function useReports(): UseReportsReturn {
  const { userId } = useSession();
  const base = useMemo(() => ieBaseUrl(), []);
  const [datasets, setDatasets] = useState<ReportDatasetMeta[]>([]);
  const [presets, setPresets] = useState<ReportPreset[]>([]);
  const [status, setStatus] = useState<LoadStatus>({ kind: 'idle' });

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (base === null || userId === null) return;
      setStatus({ kind: 'loading' });
      try {
        // Datasets are required (the builder needs them); presets are additive —
        // a presets failure must not break the from-scratch builder.
        const [dsRes, presetRes] = await Promise.all([
          fetchWithAuth(`${base}/v1/reports/datasets`),
          fetchWithAuth(`${base}/v1/reports/presets`).catch(() => null),
        ]);
        if (!dsRes.ok) {
          const body = (await dsRes.json().catch(() => null)) as ApiError | null;
          if (!cancelled)
            setStatus({ kind: 'error', detail: body?.detail ?? `HTTP ${dsRes.status}` });
          return;
        }
        const data = (await dsRes.json()) as { datasets: ReportDatasetMeta[] };
        let loadedPresets: ReportPreset[] = [];
        if (presetRes && presetRes.ok) {
          const pData = (await presetRes.json().catch(() => null)) as {
            presets: ReportPreset[];
          } | null;
          loadedPresets = pData?.presets ?? [];
        }
        if (!cancelled) {
          setDatasets(data.datasets);
          setPresets(loadedPresets);
          setStatus({ kind: 'idle' });
        }
      } catch (err) {
        if (!cancelled) {
          setStatus({
            kind: 'error',
            detail: err instanceof Error ? err.message : 'Network error',
          });
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [base, userId]);

  const runReport = useCallback(
    async (spec: ReportSpec): Promise<RunOutcome> => {
      if (base === null || userId === null) {
        return { ok: false, detail: 'Reports service is not configured.' };
      }
      try {
        const res = await fetchWithAuth(`${base}/v1/reports/run`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify(spec),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          return { ok: false, detail: body?.detail ?? `HTTP ${res.status}` };
        }
        const data = (await res.json()) as {
          dataset: string;
          columns: ReportColumn[];
          rows: Record<string, unknown>[];
          row_count: number;
          truncated: boolean;
        };
        return {
          ok: true,
          result: {
            dataset: data.dataset,
            columns: data.columns,
            rows: data.rows,
            rowCount: data.row_count,
            truncated: data.truncated,
          },
        };
      } catch (err) {
        return { ok: false, detail: err instanceof Error ? err.message : 'Network error' };
      }
    },
    [base, userId],
  );

  const exportCsv = useCallback(
    async (spec: ReportSpec): Promise<{ ok: boolean; detail?: string }> => {
      if (base === null || userId === null) {
        return { ok: false, detail: 'Reports service is not configured.' };
      }
      try {
        const res = await fetchWithAuth(`${base}/v1/reports/export`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify(spec),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          return { ok: false, detail: body?.detail ?? `HTTP ${res.status}` };
        }
        const data = (await res.json()) as { csv_base64: string; filename: string };
        triggerCsvDownload(data.csv_base64, data.filename);
        return { ok: true };
      } catch (err) {
        return { ok: false, detail: err instanceof Error ? err.message : 'Network error' };
      }
    },
    [base, userId],
  );

  return { datasets, presets, status, runReport, exportCsv };
}
